import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { VEHICLE_TYPES, FUEL_TYPES, VEHICLE_STATUSES } from "@/lib/parkingTypes";

const EMPTY_FORM = {
    vehicle_type: "car", registration_number: "", make: "", model: "", color: "",
    fuel_type: "", registration_state: "", flat_id: "", resident_id: "", parking_slot_id: "", status: "active", notes: "",
};

// Admin/Committee "Add vehicle" / "Edit vehicle" for Vehicle Management V2 — Vehicle Details +
// Ownership (Resident/Flat, reusing the existing flat_residents household) + optional Parking
// (Slot only — passes/allocation logic itself stays owned by the Parking module; this just links
// to an ALREADY-allotted slot for the chosen flat via the existing assign-slot action).
export default function VehicleFormDialog({ open, onOpenChange, flats, editing, onSaved }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [residentOptions, setResidentOptions] = useState([]);
    const [slotOptions, setSlotOptions] = useState([]);
    const [loadingFlatData, setLoadingFlatData] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setForm({
                vehicle_type: editing.vehicle_type || "car", registration_number: editing.registration_number || "",
                make: editing.make || "", model: editing.model || "", color: editing.color || "",
                fuel_type: editing.fuel_type || "", registration_state: editing.registration_state || "",
                flat_id: editing.flat_id || "", resident_id: editing.resident_id || "",
                parking_slot_id: editing.parking_slot_id || "", status: editing.status || (editing.is_active === false ? "inactive" : "active"),
                notes: editing.notes || "",
            });
        } else {
            setForm(EMPTY_FORM);
            setResidentOptions([]);
            setSlotOptions([]);
        }
    }, [open, editing]);

    useEffect(() => {
        if (!open || !form.flat_id) { setResidentOptions([]); setSlotOptions([]); return; }
        let cancelled = false;
        setLoadingFlatData(true);
        Promise.all([
            api.get(`/flats/${form.flat_id}/profile`),
            api.get(`/flats/${form.flat_id}/parking`),
        ]).then(([profileRes, parkingRes]) => {
            if (cancelled) return;
            const household = profileRes.data?.household?.active || {};
            const residents = Object.values(household).flat().map((r) => ({
                resident_id: r.resident_id, name: r.name, relationship_label: r.relationship_label,
            }));
            setResidentOptions(residents);
            setSlotOptions(parkingRes.data || []);
        }).catch((e) => { if (!cancelled) toast.error(formatError(e)); })
            .finally(() => { if (!cancelled) setLoadingFlatData(false); });
        return () => { cancelled = true; };
    }, [open, form.flat_id]);

    function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

    async function handleSave(e) {
        e.preventDefault();
        if (submitting) return;
        if (!form.flat_id) { toast.error("Please select a flat"); return; }
        setSubmitting(true);
        try {
            const basePayload = {
                vehicle_type: form.vehicle_type, registration_number: form.registration_number,
                make: form.make || null, model: form.model || null, color: form.color || null,
                fuel_type: form.fuel_type || null, registration_state: form.registration_state || null,
                resident_id: form.resident_id || null, notes: form.notes || null,
            };
            let vehicleId = editing?.id;
            if (editing) {
                await api.patch(`/parking-vehicles/${editing.id}`, { ...basePayload, status: form.status });
            } else {
                const { data } = await api.post(`/flats/${form.flat_id}/parking-vehicles`, basePayload);
                vehicleId = data.id;
            }
            const currentSlotId = editing?.parking_slot_id || "";
            if (form.parking_slot_id !== currentSlotId) {
                await api.post(`/parking-vehicles/${vehicleId}/assign-slot`, { parking_slot_id: form.parking_slot_id || null });
            }
            toast.success(editing ? "Vehicle updated" : "Vehicle added");
            onOpenChange(false);
            onSaved();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">{editing ? "Edit vehicle" : "Add vehicle"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSave} className="space-y-6">
                    <div className="space-y-3">
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-medium">Vehicle Details</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Vehicle type</Label>
                                <Select value={form.vehicle_type} onValueChange={(v) => set("vehicle_type", v)}>
                                    <SelectTrigger data-testid="vm-vehicle-type-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Registration number</Label>
                                <Input data-testid="vm-reg-input" required value={form.registration_number}
                                    onChange={(e) => set("registration_number", e.target.value)} className="rounded-sm border-brand-line font-mono" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Brand</Label>
                                <Input data-testid="vm-brand-input" value={form.make} onChange={(e) => set("make", e.target.value)} className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2">
                                <Label>Model</Label>
                                <Input data-testid="vm-model-input" value={form.model} onChange={(e) => set("model", e.target.value)} className="rounded-sm border-brand-line" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Colour</Label>
                                <Input data-testid="vm-color-input" value={form.color} onChange={(e) => set("color", e.target.value)} className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2">
                                <Label>Fuel type</Label>
                                <Select value={form.fuel_type || "__none"} onValueChange={(v) => set("fuel_type", v === "__none" ? "" : v)}>
                                    <SelectTrigger data-testid="vm-fuel-type-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Not specified" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none">Not specified</SelectItem>
                                        {FUEL_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Registration state</Label>
                            <Input data-testid="vm-reg-state-input" value={form.registration_state} onChange={(e) => set("registration_state", e.target.value)} placeholder="e.g. Gujarat" className="rounded-sm border-brand-line" />
                        </div>
                        {editing && (
                            <div className="space-y-2">
                                <Label>Status</Label>
                                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                                    <SelectTrigger data-testid="vm-status-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>{VEHICLE_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    <div className="space-y-3">
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-medium">Ownership</p>
                        <div className="space-y-2">
                            <Label>Flat</Label>
                            <Select value={form.flat_id} onValueChange={(v) => set("flat_id", v)} disabled={!!editing}>
                                <SelectTrigger data-testid="vm-flat-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Select flat" /></SelectTrigger>
                                <SelectContent className="max-h-64">
                                    {flats.map((f) => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Resident</Label>
                            <Select value={form.resident_id || "__none"} onValueChange={(v) => set("resident_id", v === "__none" ? "" : v)}
                                disabled={!form.flat_id || loadingFlatData}>
                                <SelectTrigger data-testid="vm-resident-select" className="rounded-sm border-brand-line">
                                    <SelectValue placeholder={!form.flat_id ? "Select a flat first" : "Not linked"} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none">Not linked</SelectItem>
                                    {residentOptions.map((r) => <SelectItem key={r.resident_id} value={r.resident_id}>{r.name} — {r.relationship_label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            {form.flat_id && !loadingFlatData && residentOptions.length === 0 && (
                                <p className="text-xs text-brand-inkSoft">This flat has no active residents on record yet.</p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-medium">Parking (optional)</p>
                        <div className="space-y-2">
                            <Label>Parking slot</Label>
                            <Select value={form.parking_slot_id || "__none"} onValueChange={(v) => set("parking_slot_id", v === "__none" ? "" : v)}
                                disabled={!form.flat_id || loadingFlatData || slotOptions.length === 0}>
                                <SelectTrigger data-testid="vm-parking-slot-select" className="rounded-sm border-brand-line">
                                    <SelectValue placeholder={!form.flat_id ? "Select a flat first" : "No parking slot"} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none">No parking slot</SelectItem>
                                    {slotOptions.map((s) => (
                                        <SelectItem key={s.slot_id} value={s.slot_id}>
                                            {s.slot_number}{s.vehicle && s.vehicle.id !== editing?.id ? ` (currently: ${s.vehicle.registration_number})` : ""}
                                            {s.pass ? ` · Pass ${s.pass.pass_number}` : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {form.flat_id && !loadingFlatData && slotOptions.length === 0 && (
                                <p className="text-xs text-brand-inkSoft">This flat has no allotted parking slot — the vehicle can still be saved without one.</p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Notes (optional)</Label>
                        <Textarea data-testid="vm-notes-input" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="vm-cancel-btn">Cancel</Button>
                        <Button type="submit" disabled={submitting} data-testid="vm-save-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                            {submitting ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
